import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { HeaderNavComponent } from "src/app/ui/header-nav/header-nav.component";
import { FooterComponent } from "src/app/ui/footer/footer.component";
import {
  AnalyticsService,
  AnalyticsPeriod,
  SalesAnalyticsReport,
  DrinkPerformanceReport,
  DrinkPopularityReport
} from '../../api/services/analytics.service';
import { OrderService } from '../../api/services/order.service';
import { PaymentService } from '../../api/services/payment.service';
import { StaffService } from '../../api/services/staff.service';
import { DrinksService } from '../../api/services/drinks.service';
import { RatingService } from '../../api/services/rating.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-analytics-reports',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatTableModule,
    MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatTabsModule, MatBadgeModule, MatSnackBarModule, MatToolbarModule,
    MatChipsModule, MatPaginatorModule, MatSortModule, MatProgressSpinnerModule,
    FormsModule, ReactiveFormsModule,
    HeaderNavComponent,
    FooterComponent
  ],
  templateUrl: './analytics-reports.component.html',
  styles: []
})
export class AnalyticsReportsComponent implements OnInit {
  selectedTabIndex = 0;
  selectedPeriod: AnalyticsPeriod = 'MONTHLY';
  isLoading = false;

  // Sales data
  salesReportData: SalesAnalyticsReport[] = [];
  totalRevenue = 0;
  totalOrders = 0;
  averageOrderValue = 0;
  revenueChange = 0;
  ordersChange = 0;

  // Drink/Category performance data
  drinkPerformanceData: DrinkPerformanceReport[] = [];
  categoryPerformance: any[] = [];
  topSellingDrinks: any[] = [];

  // Staff performance data
  staffPerformance: any[] = [];

  // Recent activity data
  recentActivity: any[] = [];

  // Real hourly revenue for today — filled from actual order rows in ngOnInit.
  // Map: hour (0-23) -> summed order total.
  hourlyRevenueToday: Record<number, number> = {};

  // Drink popularity
  drinkPopularityData: DrinkPopularityReport[] = [];
  drinkRevenueData: DrinkPopularityReport[] = [];

  // Revenue trends
  revenueTrends: SalesAnalyticsReport[] = [];

  // Staff ratings
  staffRatings: any[] = [];

  // Top payment methods
  topPaymentMethodsByCount: any[] = [];
  topPaymentMethodsByAmount: any[] = [];

  // Top performers from analytics service
  topPerformersList: any[] = [];

  isGeneratingReport = false;

  constructor(
    private analyticsService: AnalyticsService,
    private orderService: OrderService,
    private paymentService: PaymentService,
    private staffService: StaffService,
    private drinksService: DrinksService,
    private ratingService: RatingService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadReportData();
    this.loadRecentActivityData();
    this.loadExtendedData();
    this.loadHourlyRevenue();
  }

  /**
   * Fetch every order for this bar, keep only today's, group by
   * created-at hour and sum totals. Backs the "Revenue by Hour" widget with
   * real numbers instead of the previous 8%-of-average placeholder.
   */
  loadHourlyRevenue(): void {
    this.orderService.getOrders(0, 1000).subscribe({
      next: (page: any) => {
        const orders = page?.content || [];
        const today = new Date();
        const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
        const buckets: Record<number, number> = {};
        for (const o of orders) {
          if (!o.createdAt || o.total == null) continue;
          const ts = new Date(o.createdAt);
          if (ts.getFullYear() !== y || ts.getMonth() !== m || ts.getDate() !== d) continue;
          const h = ts.getHours();
          buckets[h] = (buckets[h] || 0) + Number(o.total);
        }
        this.hourlyRevenueToday = buckets;
      },
      error: () => { this.hourlyRevenueToday = {}; }
    });
  }

  loadExtendedData(): void {
    this.analyticsService.getRevenueTrends(this.selectedPeriod).subscribe({
      next: (data) => this.revenueTrends = data,
      error: () => {}
    });
    this.analyticsService.getDrinkPopularity().subscribe({
      next: (data) => this.drinkPopularityData = data,
      error: () => {}
    });
    this.analyticsService.getDrinkRevenue().subscribe({
      next: (data) => this.drinkRevenueData = data,
      error: () => {}
    });
    this.analyticsService.getTopPerformers(5).subscribe({
      next: (data) => this.topPerformersList = data,
      error: () => {}
    });
    this.ratingService.getStaffRatings().subscribe({
      next: (data) => this.staffRatings = data,
      error: () => {}
    });
    this.paymentService.getTopMethodsByCount(5).subscribe({
      next: (data) => this.topPaymentMethodsByCount = data,
      error: () => {}
    });
    this.paymentService.getTopMethodsByAmount(5).subscribe({
      next: (data) => this.topPaymentMethodsByAmount = data,
      error: () => {}
    });
    this.staffService.getTopPerformersByTips(0, 5).subscribe({
      next: (data) => {},
      error: () => {}
    });
  }

  generateDailyReport(): void {
    this.isGeneratingReport = true;
    this.analyticsService.generateDailyReport().subscribe({
      next: () => {
        this.isGeneratingReport = false;
        this.snackBar.open('Daily report generated', 'Close', { duration: 3000 });
      },
      error: () => {
        this.isGeneratingReport = false;
        this.snackBar.open('Failed to generate report', 'Close', { duration: 3000 });
      }
    });
  }

  generateMonthlyReport(): void {
    this.isGeneratingReport = true;
    this.analyticsService.generateMonthlyReport().subscribe({
      next: () => {
        this.isGeneratingReport = false;
        this.snackBar.open('Monthly report generated', 'Close', { duration: 3000 });
      },
      error: () => {
        this.isGeneratingReport = false;
        this.snackBar.open('Failed to generate report', 'Close', { duration: 3000 });
      }
    });
  }

  getDrinkPopularityData(): DrinkPopularityReport[] { return this.drinkPopularityData; }
  getDrinkRevenueData(): DrinkPopularityReport[] { return this.drinkRevenueData; }
  getRevenueTrends(): SalesAnalyticsReport[] { return this.revenueTrends; }
  getTopPerformersList(): any[] { return this.topPerformersList; }
  getStaffRatings(): any[] { return this.staffRatings; }
  getTopPaymentMethodsByCount(): any[] { return this.topPaymentMethodsByCount; }
  getTopPaymentMethodsByAmount(): any[] { return this.topPaymentMethodsByAmount; }

  /**
   * Load all report data based on selected period
   */
  loadReportData(): void {
    this.isLoading = true;

    forkJoin({
      sales: this.analyticsService.getSalesReport(this.selectedPeriod),
      drinks: this.analyticsService.getDrinkPerformance(this.selectedPeriod),
      staff: this.analyticsService.getStaffSalesReport(this.selectedPeriod)
    }).subscribe({
      next: (data) => {
        this.processSalesData(data.sales);
        this.processDrinkData(data.drinks);
        this.processStaffData(data.staff);
        this.isLoading = false;
        if (!environment.production) {
          console.log('✅ Analytics data loaded for period:', this.selectedPeriod);
        }
      },
      error: (error) => {
        if (!environment.production) {
          console.error('❌ Error loading analytics data:', error);
        }
        this.snackBar.open('Error loading analytics data', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  /**
   * Process sales report data from API
   */
  private processSalesData(salesData: SalesAnalyticsReport[]): void {
    this.salesReportData = salesData || [];
    
    // Calculate totals from the sales data
    this.totalRevenue = salesData.reduce((sum, item) => sum + (item.totalSales || 0), 0);
    this.totalOrders = salesData.reduce((sum, item) => sum + (item.totalOrders || 0), 0);
    this.averageOrderValue = this.totalOrders > 0 
      ? this.totalRevenue / this.totalOrders 
      : 0;

    if (!environment.production) {
      console.log('📊 Sales Data Processed:', {
        totalRevenue: this.totalRevenue,
        totalOrders: this.totalOrders,
        avgOrderValue: this.averageOrderValue
      });
    }
  }

  /**
   * Process drink performance data from API
   */
  private processDrinkData(drinkData: DrinkPerformanceReport[]): void {
    this.drinkPerformanceData = drinkData || [];

    // Build category performance from drink data
    const categoryMap = new Map<string, any>();
    drinkData.forEach(drink => {
      const category = drink.category || 'Unknown';
      if (!categoryMap.has(category)) {
        categoryMap.set(category, {
          category: category,
          drinks: 0,
          revenue: 0,
          orders: 0
        });
      }
      const categoryStats = categoryMap.get(category);
      categoryStats.drinks += 1;
      categoryStats.revenue += drink.revenueGenerated || 0;
      categoryStats.orders += drink.quantitySold || 0;
    });
    this.categoryPerformance = Array.from(categoryMap.values())
      .sort((a, b) => b.revenue - a.revenue);

    // Top selling drinks sorted by quantity sold
    this.topSellingDrinks = [...drinkData]
      .sort((a, b) => (b.quantitySold || 0) - (a.quantitySold || 0))
      .slice(0, 5)
      .map(drink => ({
        name: drink.drinkName || 'Unknown',
        category: drink.category || 'Unknown',
        price: drink.revenuePerUnit || 0,
        sales: drink.quantitySold || 0,
        revenue: drink.revenueGenerated || 0
      }));

    if (!environment.production) {
      console.log('🍺 Drink Data Processed:', this.categoryPerformance.length, 'categories,', this.topSellingDrinks.length, 'top drinks');
    }
  }

  /**
   * Process staff sales data from API
   * API Response structure:
   * { id, barId, staffId, staffName, date, ordersServed, totalSales, tipsReceived, hoursWorked, createdAt, tipsPerHour, ordersPerHour }
   * 
   * Aggregates data by staffId to ensure each staff appears only once with cumulative stats
   */
  private processStaffData(staffData: any[]): void {
    if (!staffData || staffData.length === 0) {
      this.staffPerformance = [];
      return;
    }

    // Group by staffId and aggregate values
    const staffMap = new Map<number, any>();
    
    staffData.forEach(staff => {
      const staffId = staff.staffId;
      
      if (staffMap.has(staffId)) {
        // Aggregate with existing entry
        const existing = staffMap.get(staffId);
        existing.ordersServed += staff.ordersServed || 0;
        existing.totalSales += staff.totalSales || 0;
        existing.tipsReceived += staff.tipsReceived || 0;
        existing.hoursWorked += staff.hoursWorked || 0;
        existing.entriesCount += 1;
      } else {
        // Create new entry
        staffMap.set(staffId, {
          staffId: staffId,
          name: staff.staffName || 'Unknown',
          ordersServed: staff.ordersServed || 0,
          totalSales: staff.totalSales || 0,
          tipsReceived: staff.tipsReceived || 0,
          hoursWorked: staff.hoursWorked || 0,
          entriesCount: 1
        });
      }
    });

    // Convert map to array and compute averages
    this.staffPerformance = Array.from(staffMap.values()).map(staff => ({
      ...staff,
      // Compute averages for per-hour metrics
      tipsPerHour: staff.hoursWorked > 0 ? staff.tipsReceived / staff.hoursWorked : 0,
      ordersPerHour: staff.hoursWorked > 0 ? staff.ordersServed / staff.hoursWorked : 0,
      // Legacy/computed fields for compatibility
      orders: staff.ordersServed,
      revenue: staff.totalSales
    }));

    if (!environment.production) {
      console.log('👨‍💼 Staff Data Aggregated:', this.staffPerformance.length, 'unique staff members from', staffData.length, 'entries');
    }
  }

  /**
   * Handle period change
   */
  onPeriodChange(): void {
    if (!environment.production) {
      console.log('🔄 Period changed to:', this.selectedPeriod);
    }
    this.loadReportData();
  }

  onBack(): void {
    this.router.navigate(['/service-provider']);
  }

  onTabChange(event: any): void {
    if (!environment.production) {
      console.log('🔄 Tab changed to index:', event.index);
    }
    this.selectedTabIndex = event.index;
  }

  // ===== GETTER METHODS FOR TEMPLATE =====

  getTotalRevenue(): number {
    return this.totalRevenue;
  }

  getTotalOrders(): number {
    return this.totalOrders;
  }

  getAverageOrderValue(): number {
    return this.averageOrderValue;
  }

  getTopSellingDrinks(): any[] {
    return this.topSellingDrinks;
  }

  getRevenueByHour(): any[] {
    // Real hourly revenue built from actual order rows for today.
    // Shows every hour from 12:00 to 23:00; hours with no orders show $0.
    const hourlyData: any[] = [];
    for (let hour = 12; hour <= 23; hour++) {
      hourlyData.push({
        hour: `${hour}:00`,
        revenue: Number((this.hourlyRevenueToday[hour] || 0).toFixed(2)),
      });
    }
    return hourlyData;
  }

  getCategoryPerformance(): any[] {
    return this.categoryPerformance;
  }

  getStaffPerformance(): any[] {
    return this.staffPerformance;
  }

  getCategoryPercentage(revenue: number): number {
    const maxRevenue = Math.max(...this.getCategoryPerformance().map(c => c.revenue), 1);
    return (revenue / maxRevenue) * 100;
  }

  getStaffPercentage(sales: number): number {
    const maxSales = Math.max(...this.getStaffPerformance().map(s => s.totalSales || 0), 1);
    return (sales / maxSales) * 100;
  }

  getTopPerformers(category: string): any[] {
    const staff = [...this.staffPerformance];
    switch(category) {
      case 'tips':
        return staff.sort((a, b) => (b.tipsReceived || 0) - (a.tipsReceived || 0));
      case 'orders':
        return staff.sort((a, b) => (b.ordersServed || 0) - (a.ordersServed || 0));
      case 'hours':
        return staff.sort((a, b) => (b.hoursWorked || 0) - (a.hoursWorked || 0));
      case 'revenue':
        return staff.sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0));
      default:
        return staff;
    }
  }

  /**
   * Load recent activity data from backend
   */
  loadRecentActivityData(): void {
    forkJoin({
      orders: this.orderService.getOrders(0, 1),
      payments: this.paymentService.getPayments(0, 1, 'COMPLETED'),
      staff: this.staffService.getStaffList(),
      lowStock: this.drinksService.getLowStockDrinks()
    }).subscribe({
      next: (data) => {
        const activities: any[] = [];

        // Latest order
        if (data.orders.content && data.orders.content.length > 0) {
          const latestOrder = data.orders.content[0];
          activities.push({
            type: 'order',
            description: `New order ${latestOrder.invoiceNumber || '#' + latestOrder.id}${latestOrder.tableNumber ? ' - Table ' + latestOrder.tableNumber : ''}`,
            time: latestOrder.createdAt ? this.getTimeAgo(latestOrder.createdAt) : 'Recently',
            amount: latestOrder.total,
            icon: 'receipt',
            color: 'text-green-500'
          });
        }

        // Latest completed payment
        if (data.payments && data.payments.length > 0) {
          const latestPayment = data.payments[0];
          activities.push({
            type: 'payment',
            description: `Payment completed${latestPayment.invoiceNumber ? ' - ' + latestPayment.invoiceNumber : ''}`,
            time: latestPayment.createdAt ? this.getTimeAgo(latestPayment.createdAt) : 'Recently',
            amount: latestPayment.amount,
            icon: 'credit_card',
            color: 'text-blue-500'
          });
        }

        // Most recently active staff
        if (data.staff && data.staff.length > 0) {
          const recentStaff = data.staff
            .filter(s => s.lastActive)
            .sort((a, b) => new Date(b.lastActive!).getTime() - new Date(a.lastActive!).getTime())[0];
          
          if (recentStaff && recentStaff.lastActive) {
            activities.push({
              type: 'staff',
              description: `${recentStaff.name} - Last active`,
              time: this.getTimeAgo(recentStaff.lastActive),
              amount: null,
              icon: 'person',
              color: 'text-purple-500'
            });
          }
        }

        // Low stock alert
        if (data.lowStock && data.lowStock.length > 0) {
          const lowestStock = data.lowStock[0];
          activities.push({
            type: 'inventory',
            description: `Low stock alert - ${lowestStock.name}`,
            time: 'Current',
            amount: null,
            icon: 'warning',
            color: 'text-orange-500'
          });
        }

        this.recentActivity = activities;
        if (!environment.production) {
          console.log('✅ Recent activity loaded:', activities.length, 'items');
        }
      },
      error: (error) => {
        if (!environment.production) {
          console.error('❌ Error loading recent activity:', error);
        }
        this.recentActivity = [];
      }
    });
  }

  /**
   * Convert timestamp to relative time (e.g., "2 mins ago")
   */
  private getTimeAgo(timestamp: string | Date): string {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return time.toLocaleDateString();
  }

  getRecentActivity(): any[] {
    return this.recentActivity;
  }

  // ===== ACTION METHODS =====

  exportReport(): void {
    this.snackBar.open('Exporting report...', 'Close', { duration: 3000 });
  }

  generateCustomReport(): void {
    this.snackBar.open('Custom report dialog', 'Close', { duration: 3000 });
  }

  refreshData(): void {
    if (!environment.production) {
      console.log('🔄 Refreshing analytics data...');
    }
    this.loadReportData();
    this.loadRecentActivityData();
    this.snackBar.open('Analytics data refreshed!', 'Close', { duration: 2000 });
  }
}
